import { useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Grid } from '../../../../1-primative';
import {
  Dialog, MenuItem, DialogContent, DialogTitle, Form, Button, DialogFooter, SelectField, TextField,
} from '../../../../2-component';
import { AffiliationRelations } from '../../../../../interfaces';
import { useHouseholdContext } from '../../../../../pages/household';

interface Relationship {
  type: string;
  userId: string;
  accessType: string;
}

const CREATE_USER = gql`
  mutation createUser($input: CreateUserInput!) {
    createUser(input: $input) {
      user { id }
    }
  }
`;

const INITIAL_USER_STATE = {
  firstName: '',
  lastName: '',
  middleName: '',
  accessType: 'VIEW',
  relationType: '',
  language: 'ENGLISH',
  timezone: 'America/Edmonton',
};

const AddNewClient = ({ onUserVerify }: { onUserVerify: (relationship?: Relationship | undefined) => void }) => {
  const { t } = useTranslation(['clients', 'household', 'affiliationTypes']);
  const household = useHouseholdContext();

  const [open, setOpen] = useState(false);
  const [newUser, setNewUser] = useState({ ...INITIAL_USER_STATE, organizationId: household?.orgSettings.id });

  const [createClient, { loading }] = useMutation(CREATE_USER, {
    variables: {
      input: {
        firstName: newUser.firstName,
        middleName: newUser.middleName,
        lastName: newUser.lastName,
        organizationId: newUser.organizationId,
        language: newUser.language,
        timezone: newUser.timezone,
      },
    },
  });

  const submit = async () => {
    const { data } = await createClient();
    onUserVerify({ userId: data.createUser.user.id, type: newUser.relationType, accessType: newUser.accessType });
    onClose();
  };

  const onClose = () => {
    setNewUser({ ...INITIAL_USER_STATE, organizationId: household?.orgSettings.id });
    setOpen(false);
  };

  const disabled = !newUser.firstName || !newUser.lastName || !newUser.accessType || !newUser.relationType;

  return (
    <>
      <Button type='button' variant='outlined' label={t('household:relationModal.createNewClient')} onClick={() => setOpen(true)} />
      <Dialog fullWidth maxWidth='sm' onClose={onClose} open={open}>
        <DialogTitle onClose={onClose}>{t('household:relationModal.createNewClient')}</DialogTitle>
        <Form onSubmit={submit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField label={t('addModal.firstName')} value={newUser.firstName} onChange={(e: any) => setNewUser({ ...newUser, firstName: e.target.value })} fullWidth />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField label={t('addModal.middleName')} value={newUser.middleName} onChange={(e: any) => setNewUser({ ...newUser, middleName: e.target.value })} fullWidth />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField label={t('addModal.lastName')} value={newUser.lastName} onChange={(e: any) => setNewUser({ ...newUser, lastName: e.target.value })} fullWidth />
              </Grid>
              <Grid item xs={12}>
                <SelectField
                  fullWidth
                  value={newUser?.relationType ?? ''}
                  label={t('household:relationModal.relationType')}
                  onChange={(e: any) => setNewUser({ ...newUser, relationType: e.target.value })}
                >
                  {Object.values(AffiliationRelations).map((relation, index) => (
                    <MenuItem key={index} value={relation}>{t(`affiliationTypes:affiliationRelationship.${relation}`)}</MenuItem>
                  ))}
                </SelectField>
              </Grid>
              <Grid item xs={12}>
                <SelectField fullWidth label={t('household:relationModal.permission')} onChange={(e: any) => setNewUser({ ...newUser, accessType: e.target.value })} value={newUser?.accessType ?? ''}>
                  <MenuItem value='VIEW'>{t('household:accessTypes.VIEW')}</MenuItem>
                  <MenuItem value='NO_ACCESS'>{t('household:accessTypes.NO_ACCESS')}</MenuItem>
                </SelectField>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogFooter>
            <Button type='submit' label={t('shared:add')} disabled={disabled || loading} />
          </DialogFooter>
        </Form>
      </Dialog>
    </>
  );
};

export default AddNewClient;
